/**
 * Profile rail drawer (mobile)
 *
 * Below md the profile sidebar is a Bootstrap responsive offcanvas rendered INSIDE
 * turbo frame 'profile-frame'. The backdrop is frame-owned - Offcanvas appends it to
 * the panel's parentNode (it passes rootElement: parentNode, overriding the
 * body default that Modal uses) - so a frame swap removes it with the frame. Two
 * things escape the frame and need managing here:
 *
 *   1. The body scroll lock: ScrollBarHelper writes inline overflow / padding-right
 *      on <body>, and only hide()'s completion callback (deferred ~300ms behind the
 *      transition) resets them.
 *   2. Turbo's page-cache snapshot: a frame navigation with data-turbo-action=
 *      "advance" clones <body> SYNCHRONOUSLY DURING THE CLICK (FrameController#
 *      proposeVisitIfNavigatedWithAction), and turbo:before-cache fires far too
 *      late to clean that clone. Anything still dirty at click time - the body
 *      lock, the backdrop, the panel's show/aria-modal/role - is baked into the
 *      snapshot the Back button restores. Hence the click handler tears down
 *      synchronously instead of waiting for Bootstrap's animation, mirroring
 *      bootstrap_modal_controller.js's immediate cleanup.
 *
 * NOTE: the two-property unlock is sufficient because nothing here uses
 * .fixed-top/.fixed-bottom/.is-fixed/.sticky-top (the site header's class is
 * .sticky, which ScrollBarHelper does not touch). Adopting .sticky-top anywhere
 * would leave stale padding-right on those elements after a manual unlock.
 *
 * Breakpoint crossing needs no handling: Bootstrap 5.3 closes a responsive
 * offcanvas itself on resize (its window resize listener over [aria-modal] hides
 * any panel whose computed position is no longer fixed), running the full teardown.
 */
const FRAME_ID = 'profile-frame'
const PANEL_ID = 'profile-rail'

// Whether the drawer was open when the navigation started, so focus is only moved
// for a drawer-initiated visit and desktop behaviour is untouched.
let navigatedFromDrawer = false

/**
 * Mirrors ScrollBarHelper#_resetElementAttributes for <body>, which the package
 * entry does not export. Restores whatever inline value <body> carried before the
 * lock, or removes the property when there was none. Fails safe: if Bootstrap ever
 * renames the data attributes, the property is still cleared and the page scrolls.
 */
const restore = (property, attribute) => {
  const saved = document.body.getAttribute(attribute)

  if (saved === null) {
    document.body.style.removeProperty(property)
  } else {
    document.body.removeAttribute(attribute)
    document.body.style.setProperty(property, saved)
  }
}

const unlockBody = () => {
  // Belt-and-braces: the backdrop is frame-owned and normally leaves with the
  // frame; sweeping any stragglers is free and covers exotic teardown paths.
  document.querySelectorAll('.offcanvas-backdrop').forEach((node) => node.remove())
  restore('overflow', 'data-bs-overflow')
  restore('padding-right', 'data-bs-padding-right')
}

/**
 * Strips the open state Bootstrap put on the panel itself. hide() alone is not
 * enough anywhere a snapshot or swap can observe the element within ~300ms: its
 * completion callback is deferred behind the transition, so show/hiding and
 * aria-modal/role would still be on the element when Turbo clones or caches it.
 * A restored snapshot carrying those renders a stuck-open drawer whose close
 * button is dead (a fresh Offcanvas instance boots with _isShown = false, so
 * hide() no-ops). Idempotent against Bootstrap's own deferred teardown.
 */
const resetPanel = () => {
  const panel = document.getElementById(PANEL_ID)
  if (!panel) return

  // Deactivates the focus trap and starts Bootstrap's teardown when an
  // instance is live; the strip below is what a snapshot actually sees.
  window.bootstrap?.Offcanvas.getInstance(panel)?.hide()

  panel.classList.remove('show', 'showing', 'hiding')
  panel.removeAttribute('aria-modal')
  panel.removeAttribute('role')
}

// Tapping a link inside the open drawer tears everything down synchronously -
// Turbo clones <body> for the history snapshot during this very click, so
// waiting for Bootstrap's 300ms hide would bake the open drawer and body lock
// into what the Back button restores. Delegated on document so it survives
// every frame swap without rebinding.
document.addEventListener('click', (event) => {
  const link = event.target.closest?.(`#${PANEL_ID} a[href]`)
  if (!link) return

  const panel = document.getElementById(PANEL_ID)
  // `show` only lands after the 300ms open transition; `showing` covers a tap
  // during the slide-in. Both are absent at md+, preserving the desktop early-out.
  if (!panel.matches('.show, .showing')) return

  // Arm the focus handoff only for plain clicks that will actually render the
  // frame - Log Out targets _top (no frame render to consume the flag) and a
  // cmd/ctrl-click opens a tab without navigating this page.
  const targetsFrame = (link.dataset.turboFrame || FRAME_ID) === FRAME_ID
  const plainClick = event.button === 0 &&
    !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
  navigatedFromDrawer = targetsFrame && plainClick

  resetPanel()
  unlockBody()
})

// A drawer left open without a link tap (Esc mid-fetch, a form submitting) can
// still be swapped away. Nested frames bubble through here - 'search-results'
// and friends live inside profile-frame - hence the id check, for the same
// reason profile_frame_scroll.js makes it. Idempotent.
document.addEventListener('turbo:before-frame-render', (event) => {
  if (event.target.id !== FRAME_ID) return
  unlockBody()
})

// Turbo Stream responses replace/update this frame with plain DOM ops - no
// frame render, no cache event - so the lock has to be released here too;
// 18 admin-tools stream templates target profile-frame by name.
//
// Gated on OUR panel being open rather than on the stream's target. Every one
// of the app's 63 stream templates fires this listener, and an unconditional
// unlockBody() also strips the lock Bootstrap writes for a Modal: Modal owns a
// ScrollBarHelper too (modal.js _scrollBar.hide/reset) and it writes the same
// data-bs-overflow / data-bs-padding-right on <body> that restore() clears.
// Checkout streams into an open #checkout-modal - submit_address.turbo_stream
// .erb updates checkout-modal-body on the :toolong path - so the ungated
// listener let the page scroll behind that modal and shifted its content by
// the scrollbar width. Target-gating would still be the fragile option; the
// panel is the reliable signal, and it is still in the DOM carrying its open
// class at before-stream-render (the swap has not happened yet). #profile-rail
// is the app's only offcanvas, so no other component can satisfy this check.
document.addEventListener('turbo:before-stream-render', () => {
  const panel = document.getElementById(PANEL_ID)

  // `hiding` too: a stream that detaches the panel mid-hide would otherwise
  // leave Bootstrap's deferred reset to fire against a detached element.
  if (!panel?.matches('.show, .showing, .hiding')) return

  unlockBody()
})

// Full Drive visits (Log Out targets _top). before-cache runs before Drive
// snapshots, so scrub the panel too or Back restores a stuck-open drawer.
// Also the safety net for the focus flag: a _top visit never reaches
// turbo:frame-render, and the module-level flag would otherwise survive into
// the next session and fire on an unrelated render.
document.addEventListener('turbo:before-cache', () => {
  unlockBody()
  resetPanel()
  navigatedFromDrawer = false
})

// A failed fetch renders nothing, so nothing would consume the flag.
document.addEventListener('turbo:fetch-request-error', () => {
  navigatedFromDrawer = false
})

/**
 * Focus after a drawer navigation.
 *
 * Bootstrap returns focus to the toggle on close, but only when the toggle still
 * exists; the frame swap has replaced it, so focus falls to <body> and a screen
 * reader hears nothing. The frame is the newly rendered region and the one element
 * guaranteed present on every profile page, so it takes focus. tabindex is set here
 * rather than in markup because the frame tag is written out in ~48 view files.
 */
document.addEventListener('turbo:frame-render', (event) => {
  const frame = event.target

  if (frame.id !== FRAME_ID) return
  if (!navigatedFromDrawer) return

  navigatedFromDrawer = false

  // A refused save nominates its own landing spot (the error summary), and that
  // controller has already run by now. Whoever asked to be landed on wins - same
  // rule profile_frame_scroll.js follows.
  if (frame.querySelector('[data-controller~="focus-on-load"]')) return

  frame.setAttribute('tabindex', '-1')
  frame.focus({ preventScroll: true })
})
